import { useEffect, useState, useSyncExternalStore } from 'react';
import { Plus, Trash2 } from '../../icons';
import type { TaskProjectDto } from '../../api/tasks';
import { listTeamMembers, type TeamMemberDto } from '../../api/project-teams';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import { isProjectCreator } from '../ProjectPage/confirmDeleteProject';

interface ProjectTopBarProps {
  projects: TaskProjectDto[];
  activeProjectId: string | null;
  onSelect: (id: string) => void;
  /** [새 프로젝트] 카드 — 상세 생성 모달(ProjectCreateModal v2.6) 오픈. */
  onNew: () => void;
  /** v2.8.5 — 카드 hover 휴지통 → 삭제 (미전달 시 버튼 미렌더). */
  onDelete?: (project: TaskProjectDto) => void;
}

/** 프로젝트 색 스트립 팔레트 — id 안정 해시로 결정 (색 필드 부재, 백엔드 무수정).
 *  v2.8.1: 태스크 신규/상세 모달도 동일 색 사용 — export 공유.
 *
 * ## 2026-10-11 정정 — 표를 «정본 8색»으로 바꿨다 (사용자 결정)
 *
 * ```
 * 종전 ⇒ 6색 ['#3b82f6','#f59e0b','#8b5cf6','#10b981','#ec4899','#06b6d4']
 *        ⇐ 2026-10-08 사용자 결정 ③ 「맞춥니다」로 모은 것
 * 지금 ⇒ 정본(feature/dev-syk) SPACE_COLORS 와 «같은 여덟 · 같은 순서»
 *        ⇐ 2026-10-11 사용자 원문 「정본 색을 그대로 보고 싶다」 ⇒ 길 b
 * ⇒ 즉 결정 ③ 의 «색 수»(6)는 뒤집혔고, 「색원은 한 벌」이라는 뜻은 그대로다
 *
 * 잰 것 ⇒ 이 바꿈으로 팀·프로젝트 색의 83.5% 가 바뀐다
 *   (cuid 꼴 20,000 ⇒ 그대로 16.5% · 짧은 수 1~2000 ⇒ 그대로 16.5% — 두 꼴이 같다)
 *   새로 쓰이는 두 색 ⇒ --accent-indigo 약 11.5% · --accent-orange 약 11.3%
 *   ⇒ 사용자가 그 바꿈을 보고 고른 것이다(「정본 색을 그대로」)
 *
 * ⛔ 값은 hex 로 적지 않는다 — tokens.css 토큰을 거친다(CLAUDE.md 「인라인 hex 금지」)
 *    ⇒ 값 자체는 정본과 같다. 토큰 ⇒ 값 대응은 바로 아래 각 줄의 주석이 적고 있고,
 *      그것이 정말 그 값으로 «풀리는지»는 strip-colors.guard.test.tsx 가 잰다
 *      (var() 가 안 풀리면 선언이 통째로 죽어 «색이 없는» 화면이 된다 — 눈으로 보지 않는다)
 * ⛔ 되돌리기 ⇒ 이 표 «한 곳»이다. 쓰는 13 파일은 건드리지 않았다.
 * ```
 */
const STRIP_COLORS = [
  'var(--accent-purple)', // #8b5cf6
  'var(--accent-blue)',   // #3b82f6
  'var(--accent-green)',  // #10b981
  'var(--accent-amber)',  // #f59e0b
  'var(--accent-pink)',   // #ec4899
  'var(--accent-cyan)',   // #06b6d4
  'var(--accent-indigo)', // #6366f1
  'var(--accent-orange)', // #f97316
];
export function stripColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return STRIP_COLORS[h % STRIP_COLORS.length]!;
}

/**
 * 카드 색 — 화면구성도 V0.1 (2026-08-18) 이후 사용자가 생성 화면에서 고른 색이 있으면 그것을,
 * 없으면 종전대로 id 해시 색을 쓴다. 옛 프로젝트(색 없음)의 화면은 그대로다.
 *
 * ## ⭐ 2026-10-10 — ★ 정본에서 ⭐ 들여온 두 줄이다 (⇒ 관제 허락)
 *
 * ```
 * ⇒ ★ 정본 pages/TaskPage/ProjectTopBar.tsx:25-27 ⭐ 글자 그대로다.
 * ⇒ ⚠ 우리 `pages/ProjectV2/SpaceV2Page.tsx:66-72` 에 ⇒ ⭐ 「정본의 `projectColor` 를
 *    ⇒ ⇒ ★ 베끼지 «않았다»」가 적혀 있다 — ⇒ ⭐ 그 ⛔ 의 «까닭»은 ★ 이것이었다:
 *       「⛔ 색표(STRIP_COLORS)를 ★ «두 벌»로 만들지 않는다」
 *    ⇒ ⇒ ✅ 이 함수는 ★ 색표를 ⭐ «안 만든다» ⇒ ⇒ 바로 위 `stripColor` 를 부른다
 *       ⇒ ⇒ ⇒ ★ 그러니 ⭐ 그 ⛔ 에 ★ 걸리지 않는다(⇒ 2026-10-10 관제 확인).
 * ```
 *
 * ⏳ **만료** — ★ 지금 ⭐ 같은 뜻이 ★ «두 이름»으로 있다:
 *   ```
 *   projectColor (여기)                      ⇐ 정본 이름 · pages/ProjectPage/SpaceDetailPage 가 쓴다
 *   projectAccent (pages/ProjectV2/SpaceV2Page.tsx) ⇐ 우리가 먼저 만든 지역 함수 · 몸통이 ★ 같다
 *   ```
 *   ⇒ ⇒ ★ ㉤(라우터 전환)에서 ⭐ `SpaceV2Page` 가 죽으면 ⇒ ★ `projectAccent` 가 함께 사라진다
 *      ⇒ ⇒ ⭐ 그때 ★ 이름을 ⭐ «하나»로 만든다. ⇒ ⇒ ⚠ 그 전에는 ★ 둘 다 둔다(이관 중 상태).
 */
export function projectColor(p: { id: string; color?: string | null }): string {
  return p.color ?? stripColor(p.id);
}

/**
 * ProjectTopBar (v2.7, 2026-06-11 시안 ⑤+C 확정) — /task 좌측 사이드바를 대체하는
 * 상단 가로 프로젝트 카드 바.
 *
 * 카드(C안): 컬러 스트립 + 이름 + 팀명(무소속=개인) + 멤버 아바타 스택 + done/total.
 * 아바타는 소속 팀 멤버 미리보기 (listTeamMembers 재사용, 팀당 1회·상한 10팀 —
 * 프로젝트 멤버 API 는 userId 만 반환하므로 사용자 표시 정보가 있는 팀 API 로 대체).
 * ProjectSidebar 파일은 보존 (additive 전환 — 미사용).
 */
export function ProjectTopBar({ projects, activeProjectId, onSelect, onNew, onDelete }: ProjectTopBarProps) {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [teamPreview, setTeamPreview] = useState<Record<string, TeamMemberDto[]>>({});

  useEffect(() => {
    const teamIds = [...new Set(projects.map((p) => p.teamId).filter((v): v is string => !!v))].slice(0, 10);
    if (teamIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      const results = await Promise.allSettled(teamIds.map((id) => listTeamMembers(id)));
      if (cancelled) return;
      const next: Record<string, TeamMemberDto[]> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[teamIds[i]!] = r.value;
      });
      setTeamPreview((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [projects]);

  return (
    <div className="task-topbar" role="tablist" aria-label={tTask('task.topbar.aria')}>
      {projects.map((p) => {
        const members = (p.teamId && teamPreview[p.teamId]) || [];
        const shown = members.slice(0, 3);
        const extra = members.length - shown.length;
        return (
          <span key={p.id} className="task-pcard-wrap">
          <button
            type="button"
            role="tab"
            aria-selected={p.id === activeProjectId}
            aria-label={tTask('task.topbar.card_aria', { name: p.name })}
            className={`task-pcard${p.id === activeProjectId ? ' task-pcard--on' : ''}`}
            onClick={() => onSelect(p.id)}
          >
            <span className="task-pcard-strip" style={{ background: stripColor(p.id) }} aria-hidden="true" />
            <span className="task-pcard-body">
              <span className="task-pcard-name">{p.name}</span>
              <span className="task-pcard-team">{p.teamName ?? tTask('task.topbar.personal')}</span>
              <span className="task-pcard-foot">
                <span className="task-pcard-stack" aria-hidden="true">
                  {shown.map((m) => (
                    m.avatarUrl ? (
                      <img key={m.userId} className="task-pcard-avatar" src={m.avatarUrl} alt="" />
                    ) : (
                      <span key={m.userId} className="task-pcard-avatar task-pcard-avatar--initial">
                        {m.name.slice(0, 1)}
                      </span>
                    )
                  ))}
                  {extra > 0 && (
                    <span className="task-pcard-avatar task-pcard-avatar--initial">
                      {tTask('task.topbar.more_members', { n: extra })}
                    </span>
                  )}
                </span>
                <span className="task-pcard-count">{p.doneTaskCount ?? 0}/{p.taskCount}</span>
              </span>
            </span>
          </button>
          {/* v2.9.8 — 생성자 본인만 휴지통 노출 (서버 FORBIDDEN_NOT_CREATOR 의 UI 미러) */}
          {onDelete && isProjectCreator(p) && (
            <button
              type="button"
              className="task-pcard-del"
              aria-label={tTask('task.topbar.delete_aria', { name: p.name })}
              onClick={(e) => { e.stopPropagation(); onDelete(p); }}
            >
              <Trash2 size={12} />
            </button>
          )}
          </span>
        );
      })}
      <button type="button" className="task-pcard-new" onClick={onNew}>
        <Plus size={15} />
        {tTask('task.topbar.new')}
      </button>
    </div>
  );
}
